import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from './AuthContext';
import { monitoringWebSocket } from '../services/websocketService';
import {
  FacilityBoardMessage,
  FacilityBoardMessageDetail,
  facilityBoardService,
} from '../services/facilityBoardService';
import { formatDateTimeKst } from '../utils/dateUtils';
import { axiosInstance } from '../services/axiosInstance';

async function downloadAttachmentFile(
  attachmentId: number,
  filename: string,
  poolId: number | null
) {
  const res = await axiosInstance.get(
    `/api/facility-board/attachments/${attachmentId}/download`,
    {
      params: poolId != null ? { pool_id: poolId } : undefined,
      responseType: 'blob',
    }
  );
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

interface FacilityBoardContextValue {
  unreadCount: number;
  refreshUnread: (poolId?: number | null) => Promise<void>;
  activePoolId: number | null;
  setActivePoolId: (poolId: number | null) => void;
}

const FacilityBoardContext = createContext<FacilityBoardContextValue>({
  unreadCount: 0,
  refreshUnread: async () => {},
  activePoolId: null,
  setActivePoolId: () => {},
});

export const useFacilityBoard = () => useContext(FacilityBoardContext);

export const FacilityBoardProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authState } = useAuth();
  const authenticated = Boolean(authState.isAuthenticated);
  const [activePoolId, setActivePoolId] = useState<number | null>(null);
  const [queue, setQueue] = useState<FacilityBoardMessage[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<FacilityBoardMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const current = queue[0] || null;

  const refreshUnread = useCallback(
    async (poolId?: number | null) => {
      if (!authenticated) {
        setQueue([]);
        return;
      }
      const scope = poolId !== undefined ? poolId : activePoolId;
      try {
        const list = await facilityBoardService.listUnread(scope);
        setQueue(list);
      } catch {
        /* ignore transient */
      }
    },
    [authenticated, activePoolId]
  );

  useEffect(() => {
    if (!authenticated) {
      setQueue([]);
      return;
    }
    refreshUnread();
    const t = setInterval(() => refreshUnread(), 20000);
    return () => clearInterval(t);
  }, [authenticated, refreshUnread, activePoolId]);

  useEffect(() => {
    if (!authenticated) return;

    const onMsg = (payload: any) => {
      const data = payload?.data ?? payload;
      if (data?.id && data?.title) {
        setQueue((prev) => {
          if (prev.some((p) => p.id === data.id)) return prev;
          if (data.is_read) return prev;
          return [...prev, data as FacilityBoardMessage];
        });
      } else {
        refreshUnread();
      }
    };

    monitoringWebSocket.subscribe('facility_board_message', onMsg);
    return () => monitoringWebSocket.unsubscribe('facility_board_message', onMsg);
  }, [authenticated, refreshUnread]);

  const dismissCurrent = () => {
    if (!current) return;
    setQueue((prev) => prev.filter((m) => m.id !== current.id));
  };

  const readCurrent = async () => {
    if (!current) return;
    setDetailLoading(true);
    try {
      await facilityBoardService.markRead(current.id, activePoolId);
      const d = await facilityBoardService.getDetail(current.id, activePoolId);
      setDetail(d);
      setDetailOpen(true);
      setQueue((prev) => prev.filter((m) => m.id !== current.id));
    } catch {
      dismissCurrent();
    } finally {
      setDetailLoading(false);
    }
  };

  const value = useMemo(
    () => ({
      unreadCount: queue.length,
      refreshUnread,
      activePoolId,
      setActivePoolId,
    }),
    [queue.length, refreshUnread, activePoolId]
  );

  return (
    <FacilityBoardContext.Provider value={value}>
      {children}

      <Dialog
        open={Boolean(current)}
        disableEscapeKeyDown
        onClose={() => {
          /* must choose action */
        }}
        fullWidth
        maxWidth="sm"
      >
        {current && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip size="small" color="secondary" label="시설 게시판" />
                <Typography variant="h6" component="span">
                  {current.title}
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {activePoolId != null
                  ? `${current.sender_label} · ${current.created_at ? formatDateTimeKst(current.created_at) : ''}`
                  : `${current.sender_label}${current.recipient_labels?.length ? ` · → ${current.recipient_labels.join(', ')}` : ''} · ${current.created_at ? formatDateTimeKst(current.created_at) : ''}`}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{current.body}</Typography>
              {current.attachment_count > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  첨부 {current.attachment_count}개 — 「읽기」에서 다운로드.
                </Typography>
              )}
              {queue.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  미확인 {queue.length}건 중 1번째
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={dismissCurrent}>닫기</Button>
              <Button variant="contained" onClick={readCurrent} disabled={detailLoading} autoFocus>
                읽기
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        {detail && (
          <>
            <DialogTitle>{detail.title}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {activePoolId != null
                  ? `${detail.sender_label} · ${formatDateTimeKst(detail.created_at)}`
                  : `${detail.sender_label} · ${detail.recipient_labels?.join(', ')} · ${formatDateTimeKst(detail.created_at)}`}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{detail.body}</Typography>
              {'attachments' in detail &&
                detail.attachments?.map((a) => (
                  <Typography key={a.id} variant="body2">
                    <Button
                      size="small"
                      onClick={() =>
                        downloadAttachmentFile(a.id, a.original_name, activePoolId)
                      }
                    >
                      {a.original_name}
                    </Button>
                  </Typography>
                ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>닫기</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </FacilityBoardContext.Provider>
  );
};
